import { DetailsSummaryBox } from '@dealstack/frontend/shared/lib/DetailsSummaryBox/DetailsSummaryBox.tsx';
import { TagsWithDropdown } from '@dealstack/frontend/shared/lib/TagsWithDropdown/TagsWithDropdown.tsx';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { formatValueWithUnitDenominator } from '@dealstack/shared/lib/format/number';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import { mapBlockToText } from '@dealstack/shared/lib/format/block.ts';
import { formatNumberToUI } from '@dealstack/shared/lib/format/number.ts';
import { CapTableRail, HoldingDetailRes } from '@dealstack/shared/lib/generated';
import { Flex } from '@mantine/core';
import { useMemo } from 'react';
import { DetailsSummaryBoxProps } from '@dealstack/frontend/shared/lib/DetailsSummaryBox/DetailsSummaryBox.types';

interface Props {
	data: HoldingDetailRes | null;
	// CAP-1988: which title rail this view represents. Only surfaced on nominee-structure securities.
	rail?: CapTableRail;
	hasNomineeStructure?: boolean;
	// CAP-1988: `Security.name` off the security itself rather than the holding, so the row still
	// names the security on a rail the holder holds nothing on.
	securityName?: string;
}

export const HoldingDetailsSummary = ({ data, rail, hasNomineeStructure, securityName }: Props) => {
	const tCapManage = useTranslationCapManage();

	const summaryItems: DetailsSummaryBoxProps['items'] = useMemo(() => {
		const { holderName, security, issuerName } = data || {};

		const blockBadgesValues = (security?.associatedBlocks ?? []).map((b) =>
			mapBlockToText(b, security?.securityUnitDenominator)
		);

		const interestItem = hasNomineeStructure
			? [
					{
						title: tCapManage('holding.details.summary.interest', {
							defaultValue: 'Interest',
						}),
						value:
							rail === CapTableRail.LEGAL
								? tCapManage('holding.details.summary.interest_legal', {
										defaultValue: 'Legal title',
									})
								: tCapManage('holding.details.summary.interest_beneficial', {
										defaultValue: 'Beneficial interest',
									}),
					},
				]
			: [];

		// CAP-1988: the issuer, security and holder identify the holding rather than measure it, so
		// they read the same on both rails. The figures below them are the position, and are dashed
		// rather than left blank on a rail the holder holds nothing on so the box keeps its shape
		// when the rail is toggled.
		return [
			{
				title: tCapManage('holding.details.summary.issuer'),
				value: issuerName || LONG_DASH,
			},
			{
				title: tCapManage('holding.details.summary.security'),
				value: security?.name || securityName || LONG_DASH,
			},
			{
				title: tCapManage('holding.details.summary.holder'),
				value: holderName || LONG_DASH,
			},
			...interestItem,
			{
				title: tCapManage('holding.details.summary.holding'),
				value: security?.shareHolding?.unitsAmount
					? formatValueWithUnitDenominator(
							security.shareHolding.unitsAmount,
							security.securityUnitDenominator
						)
					: LONG_DASH,
			},
			{
				title: tCapManage('holding.details.summary.votes'),
				value:
					typeof security?.shareHolding?.votesAmount === 'number'
						? formatNumberToUI(security.shareHolding.votesAmount)
						: LONG_DASH,
			},
			{
				title: tCapManage('holding.details.summary.cost'),
				value:
					typeof security?.shareHolding?.cost === 'number'
						? formatNumberToUI(security?.shareHolding?.cost, undefined, 2)
						: LONG_DASH,
			},
			{
				title: tCapManage('holding.details.summary.units'),
				children: (
					<Flex>
						{blockBadgesValues.length ? (
							<TagsWithDropdown visibleTagCount={1} tags={blockBadgesValues} />
						) : (
							LONG_DASH
						)}
					</Flex>
				),
			},
		];
	}, [data, tCapManage, rail, hasNomineeStructure, securityName]);

	return <DetailsSummaryBox items={summaryItems}></DetailsSummaryBox>;
};
